import React from 'react';

// Schematic pictures for the chooser answers, so a customer can match what is on
// their door before they answer. Front view of a door leaf; lock hardware in
// darker strokes. Keep them simple: these are for recognition, not detail.
const Leaf = ({ children, glass = false }) => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <rect x="30" y="8" width="60" height="104" rx="3" fill="#f3f4f6" stroke="#9ca3af" strokeWidth="2" />
    {glass && <rect x="38" y="16" width="28" height="88" fill="#dbeafe" stroke="#93c5fd" strokeWidth="1.5" />}
    {children}
  </svg>
);

export const PictoSlides = () => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <rect x="8" y="14" width="104" height="92" fill="#f3f4f6" stroke="#9ca3af" strokeWidth="2" />
    <rect x="14" y="20" width="44" height="80" fill="#dbeafe" stroke="#93c5fd" strokeWidth="1.5" />
    <rect x="62" y="20" width="44" height="80" fill="#dbeafe" stroke="#93c5fd" strokeWidth="1.5" />
    <rect x="56" y="48" width="6" height="26" rx="2" fill="#111827" />
    <path d="M70 108 h30 m-6 -5 l6 5 l-6 5" fill="none" stroke="#111827" strokeWidth="2.5" />
  </svg>
);

export const PictoSwings = () => (
  <Leaf glass>
    <rect x="72" y="52" width="6" height="22" rx="2" fill="#111827" />
    <path d="M90 60 a40 40 0 0 1 -18 34" fill="none" stroke="#111827" strokeWidth="2" strokeDasharray="4 3" />
    <circle cx="30" cy="20" r="3" fill="#6b7280" />
    <circle cx="30" cy="100" r="3" fill="#6b7280" />
  </Leaf>
);

// Round-bore deadbolt: a circular body with a keyway, and a separate lever below.
export const PictoBore = () => (
  <Leaf>
    <circle cx="72" cy="46" r="11" fill="#e5e7eb" stroke="#111827" strokeWidth="2.5" />
    <rect x="70.5" y="40" width="3" height="10" rx="1" fill="#111827" />
    <circle cx="72" cy="76" r="6" fill="#e5e7eb" stroke="#111827" strokeWidth="2" />
    <rect x="52" y="74" width="20" height="4" rx="2" fill="#111827" />
    <rect x="88" y="42" width="4" height="8" fill="#6b7280" />
  </Leaf>
);

// Mortice: a long faceplate in the door edge, lever on a narrow plate, keyhole under it.
export const PictoMortice = () => (
  <Leaf>
    <rect x="87" y="34" width="5" height="52" fill="#9ca3af" stroke="#111827" strokeWidth="1.5" />
    <rect x="66" y="38" width="12" height="46" rx="3" fill="#e5e7eb" stroke="#111827" strokeWidth="2" />
    <rect x="48" y="48" width="24" height="4" rx="2" fill="#111827" />
    <circle cx="72" cy="50" r="4" fill="#111827" />
    <circle cx="72" cy="70" r="3" fill="#111827" />
    <rect x="70.8" y="70" width="2.4" height="6" fill="#111827" />
  </Leaf>
);

// Euro / oval cylinder: the profile keyhole (circle over a slot) in a small plate.
export const PictoEuro = () => (
  <Leaf>
    <rect x="66" y="38" width="12" height="46" rx="3" fill="#e5e7eb" stroke="#111827" strokeWidth="2" />
    <rect x="48" y="48" width="24" height="4" rx="2" fill="#111827" />
    <circle cx="72" cy="50" r="4" fill="#111827" />
    <path d="M72 64 a5 5 0 1 1 0.1 0 M69.5 68 h5 v9 h-5 z" fill="#111827" />
    <rect x="87" y="60" width="5" height="12" fill="#9ca3af" stroke="#111827" strokeWidth="1.5" />
  </Leaf>
);

// Entrance gripset: a long pull handle on a backplate, deadbolt above it.
export const PictoGripset = () => (
  <Leaf>
    <rect x="64" y="30" width="16" height="66" rx="4" fill="#e5e7eb" stroke="#111827" strokeWidth="2" />
    <circle cx="72" cy="40" r="5" fill="#e5e7eb" stroke="#111827" strokeWidth="2.5" />
    <rect x="71" y="37" width="2" height="6" fill="#111827" />
    <path d="M72 52 v40" stroke="#111827" strokeWidth="6" strokeLinecap="round" />
    <path d="M72 52 q10 0 10 10 M72 92 q10 0 10 -10" fill="none" stroke="#111827" strokeWidth="2.5" />
  </Leaf>
);

// Night latch: a box lock on the inside face at the edge, small knob on the box.
export const PictoNightlatch = () => (
  <Leaf>
    <rect x="70" y="44" width="20" height="26" rx="2" fill="#d1d5db" stroke="#111827" strokeWidth="2.5" />
    <circle cx="78" cy="57" r="4" fill="#111827" />
    <rect x="86" y="50" width="6" height="14" fill="#6b7280" />
    <rect x="46" y="54" width="18" height="4" rx="2" fill="#111827" />
  </Leaf>
);

// More than one lock / something else: two locks stacked on the door with a question mark.
export const PictoOther = () => (
  <Leaf>
    <circle cx="72" cy="40" r="8" fill="#e5e7eb" stroke="#111827" strokeWidth="2.5" />
    <rect x="70.8" y="35" width="2.4" height="8" fill="#111827" />
    <rect x="64" y="58" width="16" height="30" rx="3" fill="#e5e7eb" stroke="#111827" strokeWidth="2" />
    <rect x="46" y="70" width="20" height="4" rx="2" fill="#111827" />
    <text x="97" y="52" textAnchor="middle" fontSize="16" fontWeight="700" fill="#111827" fontFamily="system-ui, sans-serif">?</text>
  </Leaf>
);

export const PictoBlank = () => (
  <Leaf>
    <rect x="62" y="38" width="20" height="46" rx="3" fill="none" stroke="#9ca3af" strokeWidth="2" strokeDasharray="4 3" />
    <text x="72" y="66" textAnchor="middle" fontSize="14" fill="#9ca3af" fontFamily="system-ui, sans-serif">?</text>
  </Leaf>
);

// ── The door itself: material, shelter, swing direction (Hugh, 17 Sep 2026 walk-through) ──

// Aluminium: a thin grey frame around a big pane of glass, narrow stile beside it.
export const PictoAluminium = () => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <rect x="30" y="8" width="60" height="104" rx="2" fill="#9ca3af" stroke="#374151" strokeWidth="2" />
    <rect x="37" y="15" width="46" height="90" fill="#dbeafe" stroke="#60a5fa" strokeWidth="1.5" />
    <rect x="74" y="56" width="5" height="16" rx="2" fill="#111827" />
  </svg>
);

// Timber: a warm panelled door.
export const PictoTimber = () => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <rect x="30" y="8" width="60" height="104" rx="3" fill="#d6a15c" stroke="#7c4a12" strokeWidth="2" />
    <rect x="40" y="18" width="40" height="34" rx="2" fill="none" stroke="#7c4a12" strokeWidth="2" />
    <rect x="40" y="62" width="40" height="40" rx="2" fill="none" stroke="#7c4a12" strokeWidth="2" />
    <circle cx="74" cy="60" r="3.5" fill="#111827" />
  </svg>
);

// Sheltered: the door under a porch roof.
export const PictoSheltered = () => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <path d="M10 30 L60 8 L110 30 Z" fill="#e5e7eb" stroke="#374151" strokeWidth="2" strokeLinejoin="round" />
    <rect x="14" y="30" width="92" height="6" fill="#9ca3af" />
    <rect x="18" y="36" width="5" height="76" fill="#d1d5db" stroke="#374151" strokeWidth="1.5" />
    <rect x="97" y="36" width="5" height="76" fill="#d1d5db" stroke="#374151" strokeWidth="1.5" />
    <rect x="40" y="44" width="40" height="68" rx="2" fill="#f3f4f6" stroke="#374151" strokeWidth="2" />
    <rect x="70" y="78" width="4" height="12" rx="2" fill="#111827" />
  </svg>
);

// Exposed: the door with rain on it and nothing overhead.
export const PictoExposed = () => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <path d="M20 14 q10 -14 22 0 q10 -12 22 0 q10 -10 20 2" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" />
    {[26, 40, 54, 68, 82, 96].map((x, i) => (
      <line key={x} x1={x} y1={22 + (i % 2) * 6} x2={x - 4} y2={34 + (i % 2) * 6} stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" />
    ))}
    <rect x="40" y="44" width="40" height="68" rx="2" fill="#f3f4f6" stroke="#374151" strokeWidth="2" />
    <rect x="70" y="78" width="4" height="12" rx="2" fill="#111827" />
  </svg>
);

// Plan view of the doorway: the leaf swings into the house (inward) or out onto the porch.
const Swing = ({ inward }) => (
  <svg viewBox="0 0 120 120" width="88" height="88" aria-hidden="true" className="mx-auto block">
    <text x="60" y="18" textAnchor="middle" fontSize="11" fill="#6b7280" fontFamily="system-ui, sans-serif">inside</text>
    <text x="60" y="112" textAnchor="middle" fontSize="11" fill="#6b7280" fontFamily="system-ui, sans-serif">outside</text>
    <rect x="6" y="58" width="30" height="8" fill="#9ca3af" />
    <rect x="84" y="58" width="30" height="8" fill="#9ca3af" />
    <circle cx="36" cy="62" r="3" fill="#111827" />
    {inward ? (
      <>
        <line x1="36" y1="62" x2="70" y2="28" stroke="#111827" strokeWidth="4" strokeLinecap="round" />
        <path d="M84 62 A48 48 0 0 0 70 28" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeDasharray="4 3" />
        <path d="M66 36 l4 -8 l6 6" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ) : (
      <>
        <line x1="36" y1="62" x2="70" y2="96" stroke="#111827" strokeWidth="4" strokeLinecap="round" />
        <path d="M84 62 A48 48 0 0 1 70 96" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeDasharray="4 3" />
        <path d="M66 88 l4 8 l6 -6" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
  </svg>
);
export const PictoInward = () => <Swing inward />;
export const PictoOutward = () => <Swing inward={false} />;

// ── Sliding-door hardware ──

// Hook lock: faceplate in the stile with the hook, snib lever on the face.
export const PictoHook = () => (
  <Leaf glass>
    <rect x="86" y="36" width="5" height="48" fill="#9ca3af" stroke="#111827" strokeWidth="1.5" />
    <path d="M91 52 h8 a6 6 0 0 1 0 12 h-8" fill="none" stroke="#111827" strokeWidth="2.5" />
    <rect x="70" y="40" width="12" height="40" rx="3" fill="#e5e7eb" stroke="#111827" strokeWidth="2" />
    <rect x="72" y="50" width="8" height="4" rx="2" fill="#111827" />
    <circle cx="76" cy="70" r="3" fill="#111827" />
  </Leaf>
);

// Flush pull: a recessed pull with a small latch beside it.
export const PictoPull = () => (
  <Leaf glass>
    <rect x="70" y="36" width="12" height="44" rx="6" fill="#d1d5db" stroke="#111827" strokeWidth="2" />
    <rect x="73" y="42" width="6" height="32" rx="3" fill="#9ca3af" />
    <rect x="86" y="52" width="5" height="16" fill="#9ca3af" stroke="#111827" strokeWidth="1.5" />
  </Leaf>
);

export const PICTOS = {
  slides: PictoSlides,
  swings: PictoSwings,
  bore: PictoBore,
  gripset: PictoGripset,
  nightlatch: PictoNightlatch,
  mortice: PictoMortice,
  euro: PictoEuro,
  none: PictoBlank,
  other: PictoOther,
};

// Per step, so an answer key like "none" can look different on a slider and a hinged door.
export const PICTOS_BY_STEP = {
  slides: { slides: PictoSlides, swings: PictoSwings },
  material: { aluminium: PictoAluminium, timber: PictoTimber },
  sheltered: { yes: PictoSheltered, no: PictoExposed },
  opens: { inward: PictoInward, outward: PictoOutward },
  onDoor: { bore: PictoBore, gripset: PictoGripset, nightlatch: PictoNightlatch, mortice: PictoMortice, euro: PictoEuro, none: PictoBlank, other: PictoOther },
  onSlider: { hook: PictoHook, pull: PictoPull, none: PictoBlank, other: PictoOther },
};
